import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Quote, Check, Play } from "lucide-react";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseStudyDetailPage = () => {
  const { id } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCaseStudy();
  }, [id]);

  const fetchCaseStudy = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/case-studies/${id}`);
      setCaseStudy(response.data);
    } catch (err) {
      setError("Case study not found");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "values": return "#00E5FF";
      case "training": return "#00E5FF";
      case "health_safety": return "#00E5FF";
      case "recruitment": return "#00E5FF";
      default: return "#00E5FF";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !caseStudy) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-white mb-4">Case Study Not Found</h1>
          <Link to="/case-studies">
            <Button className="bg-[#00E5FF] text-black hover:bg-[#00B8CC]">
              Back to Case Studies
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const accentColor = getCategoryColor(caseStudy.category);

  return (
    <div data-testid="case-study-detail-page" className="min-h-screen bg-[#050505]">
      {caseStudy.title && (
        <SEO
          title={`${caseStudy.title} | Case Study`}
          description={caseStudy.summary || caseStudy.challenge?.substring(0, 160)}
          keywords={`${caseStudy.category?.replace('_', ' ')}, internal communications case study, ${caseStudy.client} case study`}
          canonical={`/case-studies/${id}`}
        />
      )}
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-end" data-testid="case-study-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={caseStudy.hero_image}
            alt={caseStudy.title}
            className="w-full h-full object-cover"
            style={{ objectPosition: caseStudy.hero_position || 'top' }}
            decoding="async"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/20" />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10 pb-12 pt-32">
          {/* Back Button */}
          <Link 
            to="/case-studies" 
            className="inline-flex items-center gap-2 text-[#B3B3B3] hover:text-white transition-colors mb-6"
            data-testid="back-to-case-studies"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Case Studies
          </Link>

          <div className="max-w-4xl">
            {/* Category Badge */}
            <span 
              className="inline-block px-4 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm mb-4"
              style={{
                backgroundColor: `${accentColor}20`,
                color: accentColor
              }}
            >
              {caseStudy.category?.replace("_", " ")}
            </span>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none text-white mb-4">
              {caseStudy.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-[#B3B3B3]">
              <span className="font-semibold text-white text-lg">{caseStudy.client}</span>
              <span className="w-1.5 h-1.5 bg-[#00E5FF] rounded-full" />
              <span>{caseStudy.industry}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Summary + Challenge/Solution Combined */}
      <section className="py-10 bg-[#0a0a0a]" data-testid="case-study-content">
        <div className="container mx-auto px-4 md:px-8">
          {/* Summary */}
          <div className="max-w-4xl mb-10">
            <p className="text-xl md:text-2xl text-white leading-relaxed">
              {caseStudy.summary}
            </p>
          </div>

          {/* Challenge & Solution Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Challenge */}
            <div className="p-6 bg-[#141414] rounded-lg border-l-4 border-[#00E5FF]">
              <h2 className="font-display text-xl md:text-2xl text-[#00E5FF] mb-4">
                THE CHALLENGE
              </h2>
              <p className="text-[#B3B3B3] leading-relaxed">
                {caseStudy.challenge}
              </p>
            </div>

            {/* Solution */}
            <div className="p-6 bg-[#141414] rounded-lg border-l-4 border-[#00E5FF]">
              <h2 className="font-display text-xl md:text-2xl text-[#00E5FF] mb-4">
                THE SOLUTION
              </h2>
              <p className="text-[#B3B3B3] leading-relaxed">
                {caseStudy.solution}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Results + Services Combined */}
      <section className="py-10 bg-[#050505]" data-testid="case-study-results">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Results */}
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl md:text-3xl text-white mb-6">
                THE RESULTS
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {caseStudy.results?.map((result, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-4 bg-[#141414] rounded-lg border border-[#1F1F1F]"
                    data-testid={`result-${index}`}
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${accentColor}20` }}
                    >
                      <Check className="w-4 h-4" style={{ color: accentColor }} />
                    </div>
                    <p className="text-white text-sm">{result}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Services */}
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-white mb-6">
                SERVICES
              </h2>
              <div className="flex flex-wrap gap-2">
                {caseStudy.services_used?.map((service, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-[#1F1F1F] text-white text-sm rounded-sm font-medium"
                    data-testid={`service-${index}`}
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Videos */}
      {caseStudy.videos?.length > 0 && (
        <section className="py-10 bg-[#0a0a0a]" data-testid="case-study-videos">
          <div className="container mx-auto px-4 md:px-8">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-6">
              {caseStudy.video_title || "PROJECT VIDEOS"}
            </h2>
            <div className={`grid grid-cols-1 ${caseStudy.videos.length >= 3 ? 'md:grid-cols-3' : caseStudy.videos.length === 1 ? '' : 'md:grid-cols-2'} gap-6`}>
              {caseStudy.videos.map((video, index) => (
                <div 
                  key={index}
                  className="aspect-video bg-[#141414] rounded-lg overflow-hidden border border-[#1F1F1F]"
                  data-testid={`video-embed-${index}`}
                >
                  <iframe
                    src={video}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={`${caseStudy.title} video ${index + 1}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Labeled Gallery (large images with titles) */}
      {caseStudy.labeled_gallery?.length > 0 && (
        <section className="py-10 bg-[#050505]" data-testid="case-study-labeled-gallery">
          <div className="container mx-auto px-4 md:px-8">
            <div className="max-w-5xl mx-auto">
              {caseStudy.labeled_gallery.map((item, index) => (
                <div key={index} data-testid={`labeled-gallery-${index}`} className={index > 0 ? "mt-20" : ""}>
                  <h3 className="font-display text-xl md:text-2xl text-[#00E5FF] mb-6">
                    {item.title}
                  </h3>
                  {/* Single image */}
                  {item.image && (
                    <div className="rounded-lg overflow-hidden border border-[#1F1F1F]">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-auto"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  )}
                  {/* Multiple images gallery */}
                  {item.images && item.images.length > 0 && (
                    <div className={`grid grid-cols-2 ${item.images.length === 4 ? 'md:grid-cols-4' : item.images.length === 3 ? 'md:grid-cols-3' : ''} gap-3`}>
                      {item.images.map((img, imgIndex) => (
                        <div 
                          key={imgIndex}
                          className="bg-[#141414] rounded-lg overflow-hidden group cursor-pointer"
                        >
                          <img
                            src={img}
                            alt={`${item.title} ${imgIndex + 1}`}
                            className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {caseStudy.gallery?.length > 0 && (
        <section className="py-10 bg-[#050505]" data-testid="case-study-gallery">
          <div className="container mx-auto px-4 md:px-8">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-6">
              {caseStudy.gallery_title || "PROJECT GALLERY"}
            </h2>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${caseStudy.gallery.length % 4 === 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3`}>
              {caseStudy.gallery.map((image, index) => (
                <div 
                  key={index}
                  className="aspect-video bg-[#141414] rounded-lg overflow-hidden group cursor-pointer"
                  data-testid={`gallery-image-${index}`}
                >
                  <img
                    src={image}
                    alt={`${caseStudy.title} gallery ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {caseStudy.testimonial && (
        <section className="py-10 bg-[#0a0a0a]" data-testid="case-study-testimonial">
          <div className="container mx-auto px-4 md:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="p-8 bg-[#141414] rounded-lg border-l-4 border-[#00E5FF] relative">
                <Quote className="w-10 h-10 text-[#00E5FF] opacity-30 absolute top-6 right-6" />
                <blockquote className="font-display text-xl md:text-2xl text-white leading-relaxed mb-4 pr-12">
                  "{caseStudy.testimonial}"
                </blockquote>
                {caseStudy.testimonial_author && (
                  <cite className="text-[#00E5FF] text-base not-italic font-medium">
                    — {caseStudy.testimonial_author}
                  </cite>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Feature Section (for special highlights like open day) */}
      {caseStudy.feature_section && (
        <section className="py-10 bg-[#050505]" data-testid="case-study-feature">
          <div className="container mx-auto px-4 md:px-8">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-display text-2xl md:text-3xl text-[#00E5FF] mb-4">
                {caseStudy.feature_section.title}
              </h2>
              <p className="text-white text-lg leading-relaxed mb-8">
                {caseStudy.feature_section.description}
              </p>
              <div className="rounded-lg overflow-hidden border border-[#1F1F1F]">
                <img
                  src={caseStudy.feature_section.image}
                  alt={caseStudy.feature_section.title}
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12 bg-[#050505] relative overflow-hidden" data-testid="case-study-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full blur-[120px]" style={{ backgroundColor: accentColor }} />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl tracking-wide text-white mb-4">
              WANT RESULTS LIKE THESE?
            </h2>
            <p className="text-[#B3B3B3] mb-6">
              Let's discuss how we can create a success story for your business too.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="case-study-cta-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-5 uppercase tracking-wider"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Book a Call
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button
                  variant="outline"
                  className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-8 py-5 uppercase tracking-wider"
                >
                  View More Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetailPage;
